/**
 * ImageGallery — 이미지 갤러리 + 라이트박스 컴포넌트
 *
 * - 2/3열 그리드 레이아웃
 * - 이미지 클릭 시 createPortal 라이트박스 표시
 * - 좌우 화살표 및 ESC 키 네비게이션
 */

import { useState, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { ChevronLeft, ChevronRight, X } from 'lucide-react'
import { cn } from '../../lib/cn.js'
import styles from './ImageGallery.module.css'

export interface GalleryImage {
  src: string
  alt?: string
  caption?: string
}

interface ImageGalleryProps {
  images: GalleryImage[]
  columns?: 2 | 3
  variant?: 'card' | 'minimal' | 'elegant' | 'flush'
  onImageClick?: (index: number) => void
  className?: string
}

export function ImageGallery({ images, columns = 2, variant = 'card', onImageClick, className }: ImageGalleryProps) {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null)

  const openLightbox = (index: number) => {
    setLightboxIndex(index)
    onImageClick?.(index)
  }

  const closeLightbox = () => setLightboxIndex(null)

  const goPrev = useCallback(() => {
    setLightboxIndex((i) => (i === null ? null : (i - 1 + images.length) % images.length))
  }, [images.length])

  const goNext = useCallback(() => {
    setLightboxIndex((i) => (i === null ? null : (i + 1) % images.length))
  }, [images.length])

  useEffect(() => {
    if (lightboxIndex === null) return

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') closeLightbox()
      else if (e.key === 'ArrowLeft') goPrev()
      else if (e.key === 'ArrowRight') goNext()
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [lightboxIndex, goPrev, goNext])

  // 라이트박스 열릴 때 스크롤 잠금
  useEffect(() => {
    if (lightboxIndex !== null) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [lightboxIndex])

  const colClass = columns === 3 ? styles.cols3 : styles.cols2
  const currentImage = lightboxIndex !== null ? images[lightboxIndex] : null

  return (
    <>
      <div className={cn(styles.grid, colClass, styles[variant], className)}>
        {images.map((img, index) => (
          <div
            key={index}
            className={styles.imageWrapper}
            onClick={() => openLightbox(index)}
            role="button"
            tabIndex={0}
            aria-label={img.alt ?? `이미지 ${index + 1}`}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') openLightbox(index) }}
          >
            <img
              className={styles.image}
              src={img.src}
              alt={img.alt ?? ''}
              loading="lazy"
            />
            {img.caption && <div className={styles.caption}>{img.caption}</div>}
          </div>
        ))}
      </div>

      {lightboxIndex !== null && currentImage && createPortal(
        <div
          className={styles.lightbox}
          onClick={closeLightbox}
          role="dialog"
          aria-modal="true"
          aria-label="이미지 라이트박스"
        >
          {/* 닫기 버튼 */}
          <button
            className={styles.lightboxClose}
            onClick={closeLightbox}
            aria-label="닫기"
          >
            <X size={24} />
          </button>

          {/* 이전 버튼 */}
          {images.length > 1 && (
            <button
              className={cn(styles.lightboxNav, styles.lightboxPrev)}
              onClick={(e) => { e.stopPropagation(); goPrev() }}
              aria-label="이전 이미지"
            >
              <ChevronLeft size={24} />
            </button>
          )}

          {/* 이미지 */}
          <img
            className={styles.lightboxImage}
            src={currentImage.src}
            alt={currentImage.alt ?? ''}
            onClick={(e) => e.stopPropagation()}
          />

          {/* 다음 버튼 */}
          {images.length > 1 && (
            <button
              className={cn(styles.lightboxNav, styles.lightboxNext)}
              onClick={(e) => { e.stopPropagation(); goNext() }}
              aria-label="다음 이미지"
            >
              <ChevronRight size={24} />
            </button>
          )}

          {/* 캡션 */}
          {currentImage.caption && (
            <div className={styles.lightboxCaption}>{currentImage.caption}</div>
          )}
        </div>,
        document.body,
      )}
    </>
  )
}
